/**
 * Did the hub say this worked?
 *
 * The hub answers `{status, message, data}` in THREE dialects, measured
 * 24 Sep 2026 across efiber-admin-server (register W23): boolean at 525
 * sites, the strings 'true'/'false' at 202, and capitalised 'True'/'False'
 * at two more in camusatclient.service.ts. Eight files mix dialects
 * internally -- in user.service.ts, findOneForProfile() answers boolean
 * while the neighbouring findOneForMobile() answers a string -- so the
 * dialect is a property of the ROUTE, not of the module, and no screen can
 * safely assume one.
 *
 * Both of the naive checks are wrong against one dialect or the other:
 *
 *   if (data.status)              // 'false' is a TRUTHY string: a failure
 *                                 // renders as success. This is the client's
 *                                 // "it said it saved and it did not".
 *   if (data.status == 'true')    // true == 'true' is FALSE in JS: a success
 *                                 // renders as failure the day the hub is
 *                                 // normalised.
 *
 * So this reads the field for what it MEANS. It is deliberately tolerant
 * rather than strict, because this console ships before the hub is
 * normalised and must keep working either side of that change. The same
 * file exists in efiber-dispatch; keep the two in step.
 *
 * DO NOT simplify this to `Boolean(body.status)` until W23's hub half has
 * shipped AND the string producers are gone. Leaving it tolerant after that
 * costs nothing; removing it early re-opens the bug.
 *
 * An absent or unrecognised status reads as FAILURE. For the 47 screens that
 * compared against the string, that is exactly what they already did, so
 * nothing about them changes. TWO screens DO change, deliberately: the
 * `resetDevice` handlers in ViewUser.vue and EditUser.vue used to ask
 * `if (data.status)`, and POST /users/devices/reset answers `{status:'false'}`
 * on failure -- a truthy string -- so they announced "Device Reset
 * Successful" over a reset that had not happened. They now say it failed.
 * That is the W23 defect being fixed, not a regression.
 */
export function isOk(body) {
	const status = body?.status;
	if (status === true) return true;
	if (typeof status === 'string') return status.toLowerCase() === 'true';
	return false;
}

/**
 * The explicit opposite -- and NOT the same as `!isOk`. They differ on the
 * case that matters most: when `status` is ABSENT, `isRefusal` is false and
 * `!isOk` is true. The hub's exception envelope (opentelemetry.filter.ts)
 * carries no `status` at all, so every 401, 400 and 500 lands in exactly
 * that case.
 *
 * USE `!isOk` FOR ANY GUARD THAT PROCEEDS ON SUCCESS -- "refuse and return,
 * otherwise treat the body as the thing I asked for". Using `isRefusal`
 * there lets a 500 fall straight through into the success path, which is how
 * ConfigureMaterial.vue nearly shipped a BOM link built on an undefined
 * materialId while clearing the form as if it had saved.
 *
 * `isRefusal` is for the narrower question: did the server explicitly SAY no?
 * Use it only where "no answer" and "no" must be told apart -- for instance a
 * screen deciding whether to show an error it was never given.
 */
export function isRefusal(body) {
	const status = body?.status;
	if (status === false) return true;
	if (typeof status === 'string') return status.toLowerCase() === 'false';
	return false;
}
